import { Placement } from '@popperjs/core';
import { MutableRefObject, useMemo } from 'react';
import { usePopper } from 'react-popper';

import useCreateOptimizedPopper from '../../../../hooks/#useCreateOptimizedPopper';

interface UseDropListPopperOptions {
  targetRef: MutableRefObject<HTMLElement>;
  popperRef: MutableRefObject<HTMLDivElement>;
  placement: Placement;
  isOpened: boolean;
}

const useDropListPopper = ({ targetRef, popperRef, placement, isOpened }: UseDropListPopperOptions) => {
  const customCreatePopper = useCreateOptimizedPopper(isOpened);

  const popperOptions = useMemo<(Parameters<typeof usePopper>)[2]>(() => ({
    createPopper: customCreatePopper,
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: () => {
            // Must compensate a bug in popper...
            const targetRect = targetRef.current.getBoundingClientRect();
            const popperRect = popperRef.current.getBoundingClientRect();
            const targetOffsetWidth = targetRef.current.offsetWidth;
            const popperOffsetWidth = popperRef.current.offsetWidth;

            const badTargetScaleX = Math.round(targetRect.width) / targetOffsetWidth;
            const correctTargetScaleX = Math.ceil(targetRect.width) / targetOffsetWidth;

            const isTargetWronglyCorrected = (
              badTargetScaleX < 1 &&
              correctTargetScaleX === 1
            );

            const badPopperScaleX = Math.round(popperRect.width) / popperOffsetWidth;
            const correctPopperScaleX = Math.ceil(popperRect.width) / popperOffsetWidth;

            const isPopperWronglyCorrected = (
              badPopperScaleX < 1 &&
              correctPopperScaleX === 1
            );

            if (!isTargetWronglyCorrected && isPopperWronglyCorrected) {
              return [(popperRect.x / badPopperScaleX) - targetRect.x];
            }

            return [0, 0];
          },
        },
      },
    ],
  }), [customCreatePopper, placement, popperRef, targetRef]);

  return usePopper(
    targetRef.current,
    popperRef.current,
    popperOptions,
  );
};

export default useDropListPopper;
